// src/components/graphs/TotalMileageByProductVersion.js

import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    BarElement,
    LinearScale,
    CategoryScale,
    Tooltip,
    Legend,
    Title,
} from 'chart.js';
import { GRADIENT_COLORS } from 'constants/colors';
import PropTypes from 'prop-types';
import useWindowSize from 'hooks/useWindowResize';
import './ChartStyles.css';

// Register required components
ChartJS.register(BarElement, LinearScale, CategoryScale, Tooltip, Legend, Title);

const TotalMileageByProductVersion = ({ data }) => {
    const { width } = useWindowSize();
    const isMobile = width < 768;

    // Memoize data processing
    const productVersionData = useMemo(() => {
        if (!data || data.length === 0) {
            return {};
        }
        return data.reduce((acc, vehicle) => {
            const version = vehicle.product_version || 'N/A';
            if (!acc[version]) {
                acc[version] = {
                    totalMileage: 0,
                    count: 0,
                };
            }
            acc[version].totalMileage += vehicle.total_cummulative_mileage;
            acc[version].count += 1;
            return acc;
        }, {});
    }, [data]);

    const sortedProductVersions = useMemo(() => {
        return Object.keys(productVersionData).sort((a, b) => {
            if (a === 'N/A') return 1;
            if (b === 'N/A') return -1;
            return parseFloat(a) - parseFloat(b);
        });
    }, [productVersionData]);

    const labels = sortedProductVersions;
    const dataValues = useMemo(() => {
        return sortedProductVersions.map((version) =>
            parseFloat(productVersionData[version].totalMileage.toFixed(2))
        );
    }, [sortedProductVersions, productVersionData]);

    // Calculate dynamic chart height
    const chartHeight = 400;

    const chartData = useMemo(() => {
        return {
            labels,
            datasets: [
                {
                    label: 'Total Mileage (km)',
                    data: dataValues,
                    backgroundColor: (context) => {
                        const gradient = context.chart.ctx.createLinearGradient(
                            0,
                            0,
                            0,
                            context.chart.height
                        );
                        gradient.addColorStop(0, GRADIENT_COLORS.totalMileage.start);
                        gradient.addColorStop(1, GRADIENT_COLORS.totalMileage.end);
                        return gradient;
                    },
                    borderColor: GRADIENT_COLORS.totalMileage.border,
                    borderWidth: 1,
                    hoverBackgroundColor: GRADIENT_COLORS.totalMileage.border,
                    barThickness: 'flex',
                    maxBarThickness: 50,
                },
            ],
        };
    }, [labels, dataValues]);

    const options = useMemo(() => {
        return {
            indexAxis: 'y',
            maintainAspectRatio: false,
            responsive: true,
            scales: {
                x: {
                    type: 'linear',
                    title: {
                        display: true,
                        text: 'Total Mileage (km)',
                    },
                    ticks: {
                        font: {
                            size: isMobile ? 10 : 12,
                        },
                        callback: function (value) {
                            return value.toLocaleString();
                        },
                    },
                    beginAtZero: true,
                },
                y: {
                    type: 'category',
                    title: {
                        display: true,
                        text: 'Product Versions',
                    },
                    ticks: {
                        autoSkip: false,
                        font: {
                            size: isMobile ? 10 : 12,
                        },
                    },
                },
            },
            plugins: {
                datalabels: {
                    display: function (context) {
                        return context.dataset.data[context.dataIndex] !== 0;
                    },
                    color: 'black',
                    align: 'end',
                    anchor: 'end',
                    font: {
                        weight: 'bold',
                        size: 12,
                    },
                    formatter: (value) => `${value.toLocaleString()} km`,
                },
                tooltip: {
                    callbacks: {
                        label: function (tooltipItem) {
                            return `Total Mileage: ${new Intl.NumberFormat().format(
                                tooltipItem.raw
                            )} km`;
                        },
                    },
                },
                legend: {
                    labels: {
                        font: {
                            size: 14,
                        },
                    },
                },
            },
            animation: {
                duration: 1000,
                easing: 'easeOutBounce',
            },
        };
    }, [isMobile]);

    return (
        <div
            className="chart-wrapper"
            style={{ height: chartHeight }}
            aria-label="Total Mileage by Product Version"
        >
            {labels.length === 0 ? (
                <p>No data available.</p>
            ) : (
                <Bar data={chartData} options={options} />
            )}
        </div>
    );
};

TotalMileageByProductVersion.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            product_version: PropTypes.string,
            total_cummulative_mileage: PropTypes.number.isRequired,
        })
    ).isRequired,
};

export default React.memo(TotalMileageByProductVersion);
